import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { usePresentersQuery } from "../../hooks/queries/usePresentersQuery";
import { Presenter } from "../../types/Presenter";
import { LoadingSkeleton } from "../ui/LoadingStates";
import { ErrorMessage } from "../ui/ErrorStates";
import OptimizedContentImage from "../common/OptimizedContentImage";
import CarouselArrow from "../ui/CarouselArrow";
import { useCarousel } from "../../hooks/ui/useCarousel";
import logoImage from "../../assets/logos/e-logo-trans-40.png";
import fallbackPresenterImage from "../../assets/images/e-logo-600-min.png";

const PRESENTERS_PER_VIEW = 6;

const shuffleWithSeed = (seed: number) => {
  // Simple LCG to provide deterministic-yet-randomized order without Math.random side effects
  return (seed * 9301 + 49297) % 233280;
};

export const getRandomizedPresenters = (
  presenters: Presenter[],
  count: number,
  seed: number
): Presenter[] => {
  if (!presenters.length || count <= 0) return [];

  const shuffled = [...presenters];
  let currentSeed = seed || Date.now();

  for (let i = shuffled.length - 1; i > 0; i--) {
    currentSeed = shuffleWithSeed(currentSeed);
    const rand = currentSeed / 233280;
    const swapIndex = Math.floor(rand * (i + 1));
    [shuffled[i], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[i]];
  }

  return shuffled.slice(0, Math.min(count, shuffled.length));
};

interface PresentersWidgetContentProps {
  count?: number;
}

const PresentersWidgetContentInner: React.FC<PresentersWidgetContentProps> = ({
  count,
}) => {
  const { data: presenters = [], isLoading, error, refetch } = usePresentersQuery();
  const [shuffleSeed] = useState(() => Date.now());
  const effectiveCount = presenters.length || count || PRESENTERS_PER_VIEW;

  const visiblePresenters = useMemo(() => {
    return getRandomizedPresenters(presenters, effectiveCount, shuffleSeed);
  }, [presenters, effectiveCount, shuffleSeed]);

  const {
    scrollContainerRef,
    carouselState,
    scrollLeft,
    scrollRight,
    handleScroll,
    handleKeyDown,
    updateScrollState,
  } = useCarousel({
    scrollAmount: 2,
    animationDuration: 280,
  });

  useEffect(() => {
    updateScrollState();
  }, [visiblePresenters.length, updateScrollState]);

  if (isLoading) {
    return (
      <>
        <div className="mb-4 sm:mb-6 flex items-center justify-between">
          <LoadingSkeleton height="h-8" width="w-48" className="mb-2" />
          <LoadingSkeleton height="h-5" width="w-24" />
        </div>
        <div className="flex gap-3 sm:gap-4 overflow-hidden">
          {Array.from({ length: Math.max(4, effectiveCount) }).map((_, index) => (
            <div key={index} className="flex-shrink-0 w-32 sm:w-[11.8rem] md:w-[11.8rem] space-y-2">
              <LoadingSkeleton height="h-36" width="w-full" />
              <LoadingSkeleton height="h-4" width="w-3/4" />
            </div>
          ))}
        </div>
      </>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        type="network"
        title="Failed to Load Presenters"
        message={error.message || "Unable to load presenters. Please try again."}
        onRetry={refetch}
        showRetry={true}
        className="bg-gray-800/50 rounded-lg p-6"
      />
    );
  }

  if (!visiblePresenters.length) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-200 text-lg">No presenters available</p>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4 sm:mb-6 flex items-center justify-between gap-3">
        <h2 className="text-xl sm:text-2xl font-bold text-white">Our Presenters</h2>
        <Link
          to="/presenters"
          className="text-red-400 hover:text-red-300 text-sm font-medium transition-colors flex items-center"
        >
          View All
          <span aria-hidden="true" className="ml-1">
            &gt;
          </span>
        </Link>
      </div>

      <div
        className="relative focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:ring-offset-2 focus:ring-offset-gray-900 rounded-lg"
        role="region"
        aria-label="Our Presenters carousel"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        data-testid="presenters-carousel"
      >
        {carouselState.canScrollLeft && (
          <div className="absolute -left-6 top-1/2 -translate-y-1/2 z-20 p-4 -m-4">
            <CarouselArrow
              direction="left"
              onClick={scrollLeft}
              disabled={carouselState.isScrolling}
              className="bg-gray-900/80 border border-gray-700"
            />
          </div>
        )}

        {carouselState.canScrollRight && (
          <div className="absolute -right-6 top-1/2 -translate-y-1/2 z-20 p-4 -m-4">
            <CarouselArrow
              direction="right"
              onClick={scrollRight}
              disabled={carouselState.isScrolling}
              className="bg-gray-900/80 border border-gray-700"
            />
          </div>
        )}

        <ul
          ref={scrollContainerRef}
          className="flex gap-3 sm:gap-4 overflow-x-auto scrollbar-hide list-none"
          onScroll={handleScroll}
          aria-label="Our presenters"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            scrollBehavior: "auto",
            scrollSnapType: "none",
            willChange: "scroll-position",
            WebkitOverflowScrolling: "touch",
            touchAction: "pan-x pan-y",
            overscrollBehaviorX: "contain",
          }}
        >
          {visiblePresenters.map((presenter) => (
            <li
              key={presenter.id}
              className="flex-shrink-0 w-32 sm:w-[11.8rem] md:w-[11.8rem]"
            >
              <PresenterCard
                presenter={presenter}
                href={`/presenters/${presenter.wordpress_slug || presenter.id}`}
              />
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};

interface PresenterCardProps {
  presenter: Presenter;
  href: string;
}

const PresenterCard: React.FC<PresenterCardProps> = ({ presenter, href }) => {
  const hasImage = Boolean(presenter.image);
  const preferredSrc =
    presenter.images?.["300x300"] ||
    presenter.images?.["400x400"] ||
    presenter.images?.["150x150"] ||
    presenter.image;
  const fallback = preferredSrc || fallbackPresenterImage;
  const imageCandidates = useMemo(() => {
    const images = presenter.images || {};
    const candidates = [images["150x150"], images["300x300"], images["400x400"]]
      .filter((url): url is string => typeof url === "string" && url.length > 0)
      .map((url) => ({ src: url }));
    return candidates.length ? candidates : undefined;
  }, [presenter.images]);

  return (
    <Link
      to={href}
      className="relative block aspect-square rounded-xl sm:rounded-2xl overflow-hidden shadow-md sm:hover:shadow-xl transition-all duration-300 transform sm:hover:scale-105 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:ring-offset-2 focus:ring-offset-gray-900 group w-full"
      aria-label={`View presenter ${presenter.name}`}
    >
      <div className="absolute inset-0 bg-black">
        <OptimizedContentImage
          src={preferredSrc}
          alt={presenter.name}
          className="w-full h-full"
          imageClassName={`w-full h-full object-cover ${!hasImage ? "brightness-75" : ""}`}
          fallbackSrc={fallback}
          candidates={imageCandidates}
          sizes="189px"
        />
        {!hasImage && <div className="absolute inset-0 bg-black/60" />}
      </div>

      <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 via-black/40 to-transparent p-2 sm:p-3 z-10 flex">
        <img
          src={logoImage}
          alt="Eruption Radio"
          className="w-5 h-5 sm:w-6 sm:h-6 object-contain opacity-90 group-hover:opacity-100 transition-opacity"
          loading="lazy"
        />
      </div>

      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-2 sm:p-3 z-10">
        <p
          className="text-white font-semibold text-sm sm:text-base line-clamp-1 text-left"
          data-testid="presenter-name"
        >
          {presenter.name}
        </p>
      </div>
    </Link>
  );
};

const QueryClientBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [client] = useState(() => new QueryClient());

  try {
    useQueryClient();
    return <>{children}</>;
  } catch {
    return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
  }
};

const PresentersWidgetContent: React.FC<PresentersWidgetContentProps> = (props) => (
  <QueryClientBoundary>
    <PresentersWidgetContentInner {...props} />
  </QueryClientBoundary>
);

export default PresentersWidgetContent;
